import React, { useState } from "react";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { Link } from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import AppButton from "../../common/AppButton";
import {
  useGraduateStudent,
  useStudent,
  useTransferOutStudent,
  useWithdrawStudent
} from "../../../hooks/api/education/useStudent";
import StudentStatement from "../Billing/StudentStatement";
import StudentExitDialog, { StudentExitAction } from "./StudentExitDialog";
import { StudentStatus } from "./types/student";

const isTerminalStatus = (status?: StudentStatus | null) =>
  status === StudentStatus.WITHDRAWN ||
  status === StudentStatus.GRADUATED ||
  status === StudentStatus.TRANSFERRED_OUT;

const StudentDetail: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const { studentId = "" } = useParams();
  const { data: student, isPending, refetch } = useStudent(studentId);
  const [exitAction, setExitAction] = useState<StudentExitAction | null>(null);
  const [exitMenuAnchor, setExitMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const withdrawMutation = useWithdrawStudent();
  const graduateMutation = useGraduateStudent();
  const transferMutation = useTransferOutStudent();

  const exitPending =
    withdrawMutation.isPending ||
    graduateMutation.isPending ||
    transferMutation.isPending;

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-student", "Loading student...")}
      />
    );
  }

  if (!student) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t("student-not-found", "Student not found.")}</Typography>
      </Box>
    );
  }

  const runExit = async (reason: string) => {
    if (!student.id || !exitAction) {
      return;
    }
    const input = {
      studentId: student.id,
      expectedVersion: student.version ?? undefined,
      reason: reason || undefined
    };
    if (exitAction === "withdraw") {
      await withdrawMutation.mutateAsync(input);
    } else if (exitAction === "graduate") {
      await graduateMutation.mutateAsync(input);
    } else {
      await transferMutation.mutateAsync(input);
    }
    setExitAction(null);
    refetch();
  };

  return (
    <Box data-testid="student-detail-page" sx={{ p: 3 }}>
      <Link component={RouterLink} to="../students">
        {t("back-to-students", "Back to students")}
      </Link>
      <Typography variant="h5" component="h1" sx={{ mt: 2, mb: 1 }}>
        {student.fullName}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {student.studentCode}
        {student.status ? ` · ${student.status}` : ""}
      </Typography>
      <Typography variant="body2">
        {t("student-program-label", "Program")}: {student.programId || "—"}
      </Typography>
      <Typography variant="body2">
        {t("student-grade-label", "Grade")}: {student.gradeId || "—"}
      </Typography>
      <Typography variant="body2">
        {t("student-guardian-phone-label", "Guardian phone")}:{" "}
        {student.guardianPhone || "—"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t("student-guardian-email-label", "Guardian email")}:{" "}
        {student.guardianEmail || "—"}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <AppButton onClick={() => navigate("../students")} variant="secondary">
          {t("back", "Back")}
        </AppButton>
        {!isTerminalStatus(student.status) ? (
          <AppButton
            variant="text"
            onClick={(event) => setExitMenuAnchor(event.currentTarget)}
          >
            {t("end-enrollment", "End enrollment")}
          </AppButton>
        ) : null}
      </Box>

      <Menu
        anchorEl={exitMenuAnchor}
        open={Boolean(exitMenuAnchor)}
        onClose={() => setExitMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setExitMenuAnchor(null);
            setExitAction("withdraw");
          }}
        >
          {t("withdraw-student", "Withdraw")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setExitMenuAnchor(null);
            setExitAction("graduate");
          }}
        >
          {t("graduate-student", "Graduate")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setExitMenuAnchor(null);
            setExitAction("transferOut");
          }}
        >
          {t("transfer-student", "Transfer out")}
        </MenuItem>
      </Menu>

      <StudentExitDialog
        open={exitAction != null}
        action={exitAction ?? "withdraw"}
        studentName={student.fullName}
        loading={exitPending}
        onClose={() => setExitAction(null)}
        onConfirm={runExit}
      />

      <StudentStatement studentId={student.id ?? studentId} />
    </Box>
  );
};

export default StudentDetail;
